// Event colours (blueprint §4.1, D27). Shared by the guest page, the admin
// preview and the tests, so all three derive the same theme from the same two colours.

export const HEX_COLOR = /^#[0-9a-f]{6}$/i;

/** The guest page's own palettes (src/web/guest/styles.css), used when an event has no background colour. */
export const PAGE_PALETTES = {
  light: { "--bg": "#fcfcfa", "--surface": "#ffffff", "--ink": "#261a2e", "--ink-soft": "#6e6276", "--line": "#e7e2ea" },
  dark: { "--bg": "#17121b", "--surface": "#211a27", "--ink": "#f4eff6", "--ink-soft": "#b5a9bd", "--line": "#362c3e" },
};
const DEFAULT_LIGHT_BG = PAGE_PALETTES.light["--bg"];
const DEFAULT_DARK_BG = PAGE_PALETTES.dark["--bg"];
const DARK_INK = "#261a2e";
const LIGHT_INK = "#f4eff6";

type Rgb = [number, number, number];

function rgb(hex: string): Rgb {
  return [1, 3, 5].map((i) => parseInt(hex.slice(i, i + 2), 16)) as Rgb;
}

function toHex([r, g, b]: Rgb): string {
  return "#" + [r, g, b].map((c) => Math.round(c).toString(16).padStart(2, "0")).join("");
}

/** `weight` of `a` mixed with the rest of `b`, like CSS color-mix in sRGB. */
export function mix(a: string, b: string, weight: number): string {
  const [x, y] = [rgb(a), rgb(b)];
  return toHex(x.map((c, i) => c * weight + y[i] * (1 - weight)) as Rgb);
}

/** WCAG relative luminance, 0 (black) to 1 (white). */
export function luminance(hex: string): number {
  const [r, g, b] = rgb(hex).map((c) => {
    const s = c / 255;
    return s <= 0.04045 ? s / 12.92 : ((s + 0.055) / 1.055) ** 2.4;
  });
  return 0.2126 * r + 0.7152 * g + 0.0722 * b;
}

/** WCAG contrast ratio, 1 to 21. */
export function contrast(a: string, b: string): number {
  const [hi, lo] = [luminance(a), luminance(b)].sort((x, y) => y - x);
  return (hi + 0.05) / (lo + 0.05);
}

/** A background is "dark" when light text reads better on it than dark text. */
export const isDark = (bg: string) => contrast(bg, LIGHT_INK) > contrast(bg, DARK_INK);

/** The accent as text on `bg`: the accent itself if it reads (4.5:1), else moved toward the ink. */
export function accentInk(accent: string, bg: string): string {
  const ink = isDark(bg) ? LIGHT_INK : DARK_INK;
  for (let w = 1; w > 0; w -= 0.05) {
    const candidate = mix(accent, ink, w);
    if (contrast(candidate, bg) >= 4.5) return candidate;
  }
  return ink;
}

export interface Theme {
  dark: boolean;
  vars: Record<string, string>;
}

/**
 * CSS custom properties for the guest page. `background` null means the event
 * has none, and `prefersDark` picks between the page's own light and dark palettes.
 */
export function guestTheme(accent: string, background: string | null, prefersDark: boolean): Theme {
  const bg = background ?? (prefersDark ? DEFAULT_DARK_BG : DEFAULT_LIGHT_BG);
  const dark = isDark(bg);
  const ink = dark ? LIGHT_INK : DARK_INK;
  const vars: Record<string, string> = {
    "--accent": accent,
    "--on-accent": contrast(accent, "#ffffff") >= contrast(accent, DARK_INK) ? "#ffffff" : DARK_INK,
    "--accent-ink": accentInk(accent, bg),
  };
  if (background) {
    Object.assign(vars, {
      "--bg": bg,
      "--surface": dark ? mix("#ffffff", bg, 0.06) : mix("#ffffff", bg, 0.6),
      "--ink": ink,
      "--ink-soft": mix(ink, bg, 0.68),
      "--line": mix(ink, bg, dark ? 0.18 : 0.13),
      "--error": dark ? "#ef6b62" : "#b3261e",
      "--ok": dark ? "#5fae70" : "#2e6b3c",
    });
  }
  return { dark, vars };
}

/** Under 3:1 the accent's buttons and outlines blend into the background. */
export const accentClashes = (accent: string, bg: string) => contrast(accent, bg) < 3;

/** Mid-tone backgrounds (neither light nor dark) leave body text under 7:1 with either ink. */
export const backgroundIsMuddy = (bg: string) => Math.max(contrast(bg, LIGHT_INK), contrast(bg, DARK_INK)) < 7;
